import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Grid,
    Button,
    Divider,
    Snackbar,
    Alert,
    MenuItem,
    CircularProgress,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

const API_BASE_URL = "https://api.hr-ebnhaian.cloud/api/v1";

const Section = ({ title, children }) => (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography fontWeight="bold">{title}</Typography>
        <Divider sx={{ my: 2 }} />
        {children}
    </Paper>
);

export default function AddDepartment() {
    const [snack, setSnack] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [centers, setCenters] = useState([]);
    const [loadingCenters, setLoadingCenters] = useState(false);

    useEffect(() => {
        const fetchCenters = async () => {
            try {
                setLoadingCenters(true);
                const res = await axios.get(`${API_BASE_URL}/centers`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                setCenters(res.data?.data || []);
            } catch (err) {
                setSnack({
                    open: true,
                    message: err.response?.data?.message || "Failed to load centers ❌",
                    severity: "error",
                });
            } finally {
                setLoadingCenters(false);
            }
        };
        fetchCenters();
    }, []);

    const schema = Yup.object({
        name: Yup.string()
            .min(3, "Too short")
            .max(32, "Too long")
            .required("Department name is required"),
        // ✅ center مطلوب فقط إذا مو allCenters
        centers: Yup.array().when("allCenters", {
            is: true,
            then: (s) => s.notRequired(),
            otherwise: (s) => s.min(1, "At least one center is required"),
        }),
        allCenters: Yup.boolean(),
    });

    const formik = useFormik({
        initialValues: {
            name: "",
            centers: [],
            allCenters: false, // ✅ NEW
        },
        validationSchema: schema,

        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                const token = localStorage.getItem("token");
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const name = values.name.trim();

                let selectedCenters = [];

                if (values.allCenters) {
                    selectedCenters = centers.map((c) => c._id);
                } else {
                    selectedCenters = values.centers;
                }

                if (!selectedCenters.length) {
                    setSnack({
                        open: true,
                        message: "Please select at least one center ❌",
                        severity: "error",
                    });
                    return;
                }

                const requests = selectedCenters.map((centerId) =>
                    axios.post(
                        `${API_BASE_URL}/departments`,
                        { name, center: centerId },
                        { headers: { "Content-Type": "application/json", ...headers } }
                    )
                );

                const results = await Promise.allSettled(requests);

                const successCount = results.filter((r) => r.status === "fulfilled").length;
                const failed = results.filter((r) => r.status === "rejected");
                const failMsg = failed[0]?.reason?.response?.data?.message;

                setSnack({
                    open: true,
                    severity: failed.length ? "warning" : "success",
                    message: failed.length
                        ? `Added to ${successCount}/${selectedCenters.length} centers ✅ (some failed: ${failMsg || "duplicates or validation"})`
                        : `Added to ${successCount} centers ✅`,
                });

                resetForm();
            } catch (err) {
                setSnack({
                    open: true,
                    message: err.response?.data?.message || "Unexpected error occurred ❌",
                    severity: "error",
                });
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
            <Typography variant="h5" fontWeight="bold" mb={3}>
                Add Department
            </Typography>

            <form onSubmit={formik.handleSubmit}>
                <Section title="Department Information">
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Department Name"
                                fullWidth
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formik.values.allCenters}
                                        onChange={(e) => {
                                            formik.setFieldValue("allCenters", e.target.checked);
                                            if (e.target.checked) formik.setFieldValue("centers", []);
                                        }}
                                    />
                                }
                                label="Apply to all centers"
                            />

                            <TextField
                                select
                                label="Centers"
                                fullWidth
                                name="centers"
                                value={formik.values.centers}
                                onChange={formik.handleChange}
                                disabled={formik.values.allCenters}
                                error={formik.touched.centers && Boolean(formik.errors.centers)}
                                helperText={
                                    formik.values.allCenters
                                        ? "Disabled because 'Apply to all centers' is enabled."
                                        : formik.touched.centers && formik.errors.centers
                                }
                                SelectProps={{
                                    multiple: true,
                                    displayEmpty: true,
                                }}
                            >
                                {centers.map((c) => (
                                    <MenuItem key={c._id} value={c._id}>
                                        {c.name} {c.code ? `(${c.code})` : ""}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {loadingCenters && (
                                <Box mt={1} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography fontSize={12} sx={{ opacity: 0.7 }}>
                                        Fetching centers...
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Section>

                <Button variant="contained" color="success" type="submit" disabled={formik.isSubmitting}>
                    Save Department
                </Button>
            </form>

            <Snackbar
                open={snack.open}
                autoHideDuration={4500}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert
                    severity={snack.severity}
                    variant="filled"
                    onClose={() => setSnack({ ...snack, open: false })}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}